import { isTopconManufacturer } from "./fluidManualBrush";

export const TOPCON_OPT_SOURCE_WIDTH = 512;
export const TOPCON_OPT_SOURCE_HEIGHT = 992;
export const TOPCON_OPT_TARGET_ASPECT = 2.73;
export const TOPCON_OPT_DISPLAY_SCALE_X =
  (TOPCON_OPT_TARGET_ASPECT * TOPCON_OPT_SOURCE_HEIGHT) / TOPCON_OPT_SOURCE_WIDTH;
export const TOPCON_OPT_DISPLAY_SCALE_Y = 1.0;

const topconOptDisplayStretchUrls = new Set();
let imageLoaderInstalled = false;

export function getManufacturerFromDataSet(dataSet) {
  if (!dataSet || typeof dataSet.string !== "function") {
    return "";
  }
  return String(dataSet.string("x00080070") || "").trim();
}

export function shouldApplyTopconOptDisplayStretch(manufacturer, width, height) {
  return (
    isTopconManufacturer(manufacturer) &&
    Number(width) === TOPCON_OPT_SOURCE_WIDTH &&
    Number(height) === TOPCON_OPT_SOURCE_HEIGHT
  );
}

export function registerTopconOptDisplayStretchUrl(url) {
  if (!url) return;
  topconOptDisplayStretchUrls.add(url);
}

export function isTopconOptDisplayStretchUrl(url) {
  if (!url) return false;
  return topconOptDisplayStretchUrls.has(url);
}

export function getTopconOptDisplayTransform() {
  return {
    scaleX: TOPCON_OPT_DISPLAY_SCALE_X,
    scaleY: TOPCON_OPT_DISPLAY_SCALE_Y,
    targetAspect: TOPCON_OPT_TARGET_ASPECT,
    sourceWidth: TOPCON_OPT_SOURCE_WIDTH,
    sourceHeight: TOPCON_OPT_SOURCE_HEIGHT,
  };
}

/**
 * Returns horizontal display scale for a Cornerstone image when Topcon stretch
 * is active (columnPixelSpacing / rowPixelSpacing), otherwise 1.
 */
export function getTopconOptDisplayScaleX(image) {
  if (!image) return 1;

  const row = image.rowPixelSpacing;
  const col = image.columnPixelSpacing;

  if (!row || !col || row <= 0) return 1;

  const ratio = col / row;
  if (Math.abs(ratio - TOPCON_OPT_DISPLAY_SCALE_X) < 0.001) {
    return ratio;
  }

  return 1;
}

/**
 * Apply anisotropic display stretch via pixel spacing — pixel matrix stays 512×992.
 */
export function applyTopconOptDisplayStretchToCornerstoneImage(image) {
  if (!image || typeof image.getPixelData !== "function") {
    return image;
  }

  const srcWidth = image.columns || image.width;
  const srcHeight = image.rows || image.height;

  if (
    srcWidth !== TOPCON_OPT_SOURCE_WIDTH ||
    srcHeight !== TOPCON_OPT_SOURCE_HEIGHT
  ) {
    return image;
  }

  const row = image.rowPixelSpacing || 1;
  image.rowPixelSpacing = row;
  image.columnPixelSpacing = row * TOPCON_OPT_DISPLAY_SCALE_X;
  image.topconDisplayStretch = true;

  return image;
}

export function applyTopconOptDisplayStretchMetadata(metadata) {
  if (!metadata?.meta) {
    return metadata;
  }

  metadata.meta = {
    ...metadata.meta,
    width: TOPCON_OPT_SOURCE_WIDTH,
    height: TOPCON_OPT_SOURCE_HEIGHT,
    displayStretch: true,
  };
  metadata.displayTransform = getTopconOptDisplayTransform();

  return metadata;
}

export function applyTopconOptDisplayStretchToStack(opData, optData) {
  if (!opData || !optData?.metadata?.meta) {
    return false;
  }

  const manufacturer =
    getManufacturerFromDataSet(opData.metadata?.dataset) ||
    getManufacturerFromDataSet(optData.metadata?.dataset);

  const { width, height } = optData.metadata.meta;

  if (!shouldApplyTopconOptDisplayStretch(manufacturer, width, height)) {
    return false;
  }

  registerTopconOptDisplayStretchUrl(optData.url);
  applyTopconOptDisplayStretchMetadata(optData.metadata);
  return true;
}

export function installTopconOptDisplayStretchImageLoader(
  cornerstone,
  parseImageId,
) {
  if (imageLoaderInstalled || !cornerstone || typeof parseImageId !== "function") {
    return;
  }

  const originalLoadAndCacheImage =
    cornerstone.loadAndCacheImage.bind(cornerstone);
  const originalLoadImage = cornerstone.loadImage.bind(cornerstone);

  const transformLoadedImage = (imageId, image) => {
    try {
      const parsed = parseImageId(imageId);
      if (!isTopconOptDisplayStretchUrl(parsed?.url)) {
        return image;
      }
      return applyTopconOptDisplayStretchToCornerstoneImage(image);
    } catch (_error) {
      return image;
    }
  };

  cornerstone.loadAndCacheImage = (imageId, options) =>
    originalLoadAndCacheImage(imageId, options).then((image) =>
      transformLoadedImage(imageId, image),
    );

  cornerstone.loadImage = (imageId, options) =>
    originalLoadImage(imageId, options).then((image) =>
      transformLoadedImage(imageId, image),
    );

  imageLoaderInstalled = true;
}

/** @deprecated Use installTopconOptDisplayStretchImageLoader */
export const installTopconOptLetterboxImageLoader =
  installTopconOptDisplayStretchImageLoader;

/** Disable canvas interpolation so horizontal stretch uses nearest-neighbor columns. */
export function applyTopconOptNearestNeighborCanvas(cornerstone, element) {
  if (!element || !cornerstone) return;
  try {
    const enabledElement = cornerstone.getEnabledElement(element);
    const ctx = enabledElement?.canvas?.getContext?.("2d");
    if (ctx) {
      ctx.imageSmoothingEnabled = false;
    }
  } catch (_error) {
    // Element may not be enabled yet
  }
}
